import { AnimatePresence, motion } from 'framer-motion';
import { isFunction } from 'lodash';
import { useQueryState } from 'nuqs';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Button } from '@/components/button';
import { Map } from '@/components/map';
import { AdvancedSearchDropdown } from '@/components/modules/advanced-search/dropdown';
import { AdvancedSearchSummary } from '@/components/modules/advanced-search/summary';
import { RouteList } from '@/components/modules/route/list';
import { SearchBar } from '@/components/modules/search/bar';
import { SearchLimitMessage } from '@/components/modules/search/limit-message';
import { SearchMessage } from '@/components/modules/search/message';
import { LoadingSpinner } from '@/components/ui/loading/spinner';
import { UserMessage } from '@/components/ui/user-message';
import { DEFAULT_NUQS_CONFIG } from '@/lib/constants';
import { PortalContainer } from '@/lib/context/portal-container';
import { routesCountState } from '@/state/routes';
import {
  advancedSearchFormOpenState,
  errorMessageState,
  hasSearchedState,
  isSearchingRoutesState,
  newSearchIsNeededState,
  searchLimitReachedState,
} from '@/state/search';
import { isProductionEnv } from '@/utils/helpers';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';
import { parseAsAdvancedSearch } from '@/utils/nuqs/parse-as-advanced-search';
import { tv } from '@/utils/styles';

const IS_NOT_PRODUCTION = !isProductionEnv();

export interface SearchRoutesProps {
  onSearch: () => void;
  onForceSearch?: () => void;
  onClickSaveItem: () => void;
}

export const SearchRoutes = ({ onSearch, onForceSearch, onClickSaveItem }: SearchRoutesProps) => {
  const isSearching = useRecoilValue(isSearchingRoutesState);
  const hasSearched = useRecoilValue(hasSearchedState);
  const isNewSearchNeeded = useRecoilValue(newSearchIsNeededState);
  const isSearchLimitReached = useRecoilValue(searchLimitReachedState);
  const routesCount = useRecoilValue(routesCountState);
  const [advancedSearchIsOpen, setAdvancedSearchIsOpen] = useRecoilState(advancedSearchFormOpenState);
  const errorMessage = useRecoilValue(errorMessageState);

  const [searchParams] = useQueryState(
    QUERY_PARAM.ADVANCED_SEARCH,
    parseAsAdvancedSearch.withOptions(DEFAULT_NUQS_CONFIG),
  );

  const isForceSearchEnabled = isFunction(onForceSearch) && IS_NOT_PRODUCTION;

  const { base, layout, layoutContent, layoutMap, searchMessage } = styles();

  return (
    <div className={base()}>
      <SearchBar onSearch={onSearch} onClickSaveItem={onClickSaveItem} />
      <div className={layout()}>
        <div className={layoutContent()}>
          {isForceSearchEnabled ? (
            <Button onClick={onForceSearch} size="small" className="absolute right-4 top-6">
              Force search
            </Button>
          ) : null}
          {isSearchLimitReached ? (
            <div className={searchMessage()}>
              <SearchLimitMessage />
            </div>
          ) : (
            <>
              <AdvancedSearchSummary onSearch={onSearch} searchParams={searchParams} />
              {Boolean(!isSearching && !hasSearched && !errorMessage) && (
                <div className={searchMessage()}>
                  <SearchMessage
                    heading="Discover all the possible ways to get your cargo from A → B, quickly, efficiently and on time."
                    body="Simply enter a desired Origin &amp; Destination and press Search to start exploring possible shipping routes by Ocean, Air and Land."
                  />
                </div>
              )}
              <LoadingSpinner text="route data" isVisible={isSearching} top="30%" />
              <AnimatePresence>
                {Boolean(isNewSearchNeeded && routesCount) && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={{
                      visible: {
                        opacity: 1,
                        scale: 1,
                        display: 'block',
                        transition: {
                          delay: 0.1, // Needed to stop the flicker of this user message appearing when the location autocomplete is flicking between searching and not searching
                        },
                      },
                      hidden: {
                        display: 'none',
                        opacity: 0,
                        scale: 0.9,
                      },
                    }}
                  >
                    <UserMessage
                      body="You made changes that require a new search, click the search button to see updated results"
                      variant="warning"
                      className="my-0 max-w-none py-0 xl:my-0"
                      noIcon
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              {Boolean(routesCount) && <RouteList />}
              {/* An error occurred - only show on dev */}
              {errorMessage && IS_NOT_PRODUCTION && <UserMessage heading={errorMessage} variant="error" />}
              {/* No results */}
              {Boolean(!routesCount && !isSearching && hasSearched && !errorMessage) && (
                <div className={searchMessage()}>
                  <SearchMessage
                    image="search-error"
                    heading="Sorry, we were not able to find any routes that matched your query."
                    body="Try a different search query or try clearing your advanced filters (if any are applied)."
                  />
                </div>
              )}
              {/* No results, because of error */}
              {Boolean(!isSearching && errorMessage) && (
                <div className={searchMessage()}>
                  <SearchMessage
                    image="search-error"
                    heading="Sorry, it looks like something has gone wrong."
                    body="Try that search again or try changing your query. If this issues, please let us know."
                  />
                </div>
              )}
            </>
          )}
        </div>
        <PortalContainer className={layoutMap()}>
          <Map />
        </PortalContainer>
      </div>
      {advancedSearchIsOpen && (
        <AdvancedSearchDropdown
          onSearch={() => {
            setAdvancedSearchIsOpen(false);
            onSearch();
          }}
        />
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative overflow-auto md:grid md:h-full md:grid-rows-[var(--app-search-bar-height-lg)_1fr] md:overflow-hidden',
    layout:
      'h-full bg-grey-100 md:grid md:grid-cols-[minmax(600px,_60%)_1fr] md:overflow-hidden xl:grid-cols-[900px_1fr]',
    layoutContent: 'relative z-2 overflow-auto md:shadow',
    layoutMap: 'z-1 hidden h-full md:block',
    searchMessage: 'grid w-full place-items-center px-6 py-24 sm:px-2 sm:py-32',
  },
});
